import React, { useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import StatsRow from '../assignments/components/StatsRow';
import AssociateSidebar from '../assignments/components/AssociateSidebar';
import ProcessPathSection from '../assignments/components/ProcessPathSection';
import DecisionInfoModal from '../assignments/components/DecisionInfoModal';
import {
  AssignmentAssociate,
  ProcessPath,
  isEmptySlot,
} from '../assignments/models';
import {
  filterUnassignedAssociates,
  getPALeadHRPool,
  findAssignedAssociate,
  isAssigned,
} from '../assignments/utils';

/**
 * Flattened, single-half view of a published plan.
 *
 * The backend `GetStationAssignmentsResponse` carries both halves of the
 * shift (`firstHalfStationAssignment` / `secondHalfStationAssignment`). The
 * read-only published screen renders one half at a time, so the container
 * resolves the active half and passes this flat view-model down — the
 * screen never reaches into the half-nested response itself.
 *
 * The headline counts (Scheduled / Assigned / Unassigned / Overrides) are
 * derived here from `associates` + `processPaths`, mirroring the editable
 * Assignments page, so the screen carries no separate metrics blob.
 */
export interface PublishedAssignmentView {
  associates: AssignmentAssociate[];
  /**
   * PA/Lead/HR associates surfaced as a separate read-only box below the
   * Unassigned box, for parity with the editable Assignments page. Optional:
   * absent/empty when the published plan has no such associates.
   */
  unassignedPALeadHRAssociates?: AssignmentAssociate[];
  processPaths: ProcessPath[];
  lastModifiedBy: string;
  lastModifiedDate: string;
}

/**
 * Props for {@link PublishedAssignmentScreen}.
 *
 * `searchQuery` and `onSearchChange` are owned by the container so the
 * screen stays a pure render of the supplied response. `planId` is part
 * of the screen's contract for future use (e.g. wiring deep links from
 * within the screen) even though it does not directly drive the current
 * render — the page header, which displays the Plan_Id, is composed by
 * the container, not by this screen.
 */
export interface PublishedAssignmentScreenProps {
  response: PublishedAssignmentView;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  planId: string;
}

/**
 * Presentational screen for the Published Assignment view.
 *
 * Renders the redesigned Station Assignment UI — the same components the
 * editable Assignments page uses ({@link StatsRow}, {@link AssociateSidebar},
 * {@link ProcessPathSection}) — but non-editable:
 *
 *   - No `onAssignAssociate` is given to the sidebar, so associate rows carry
 *     no "Assign" button. The search field and unassigned-count badge remain
 *     functional (display-only).
 *   - Each `ProcessPathSection` is rendered with `readOnly`, which the section
 *     forwards to its `WorkstationCard` and pool children so no inline "Add
 *     associate" typeahead, unassign (✕), or move (↗) affordance renders, and
 *     empty (planned-but-unfilled) slots show a neutral "Unassigned" label.
 *   - The only interactive control is the view-decision (ℹ️) action, which
 *     opens a read-only {@link DecisionInfoModal}. It mutates nothing.
 *   - No Verify / Submit / Publish action, no assign/unassign modal, and no
 *     Redux dispatch — the screen is a pure renderer of the supplied response.
 *
 * The unassigned-associate pool is derived from the supplied response using
 * the same pure helpers (`isAssigned`, `filterUnassignedAssociates`) as the
 * editable Assignments page so the filter semantics are identical.
 *
 * The page header (title, back-navigation, plan metadata) is composed by
 * the container — it is intentionally not rendered here.
 */
const PublishedAssignmentScreen: React.FC<PublishedAssignmentScreenProps> = ({
  response,
  searchQuery,
  onSearchChange,
}) => {
  // The PA/Lead/HR box keeps its own local search text so searching it never
  // filters the Unassigned box (whose search is container-owned) and vice versa.
  const [paLeadHRSearchQuery, setPALeadHRSearchQuery] = useState<string>('');

  // Each process path defaults to expanded on first render, mirroring the
  // editable Assignments page. The collapse/expand toggle remains functional
  // in read-only mode because it is display-only — it does not mutate plan
  // data.
  const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>(
    () => {
      const initial: Record<string, boolean> = {};
      response.processPaths.forEach((path) => {
        initial[path.fclmFunctionId] = true;
      });
      return initial;
    },
  );

  // The associate whose assignment decision is being viewed (info ℹ️ action).
  // Non-null means the read-only DecisionInfoModal is open for that associate.
  const [viewingDecisionAssociate, setViewingDecisionAssociate] =
    useState<AssignmentAssociate | null>(null);

  const processPaths: ProcessPath[] = response.processPaths;

  /**
   * The unassigned pool: every associate from the canonical master list
   * that does not appear in any workstation or indirect-path list.
   */
  const unassignedAssociates: AssignmentAssociate[] = useMemo(
    () => response.associates.filter((a) => !isAssigned(a, response.processPaths)),
    [response.associates, response.processPaths],
  );

  const filteredUnassignedAssociates = useMemo(
    () => filterUnassignedAssociates(unassignedAssociates, searchQuery),
    [unassignedAssociates, searchQuery],
  );

  /**
   * The PA/Lead/HR pool, derived and filtered the same way as the editable
   * page so the read-only "PA / Lead / HR" box matches. Reuses the new
   * `AssociateSidebar` with no action callbacks (view-only).
   */
  const paLeadHRAssociates: AssignmentAssociate[] = useMemo(
    () =>
      getPALeadHRPool({
        unassignedPALeadHRAssociates: response.unassignedPALeadHRAssociates,
        processPaths: response.processPaths,
      }),
    [response.unassignedPALeadHRAssociates, response.processPaths],
  );

  const filteredPALeadHRAssociates = useMemo(
    () => filterUnassignedAssociates(paLeadHRAssociates, paLeadHRSearchQuery),
    [paLeadHRAssociates, paLeadHRSearchQuery],
  );

  /**
   * Headline counts for the StatsRow, derived the same way as the editable
   * Assignments page. `assigned` counts only real associates in the
   * authoritative path/workstation structure (empty planned slots don't
   * inflate it); `unassigned` reuses the derived pool; `scheduled` is their
   * sum. `overrides` is kept at 0 for parity with the editable page, which
   * tracks overrides only per session — a read-only historical view has no
   * session, so no override count is surfaced.
   */
  const stats = useMemo(() => {
    const countReal = (entries: AssignmentAssociate[]) =>
      entries.filter((a) => !isEmptySlot(a)).length;

    const assigned = processPaths.reduce(
      (sum, path) =>
        sum +
        path.workstations.reduce((wsSum, ws) => wsSum + countReal(ws.assignedAssociates), 0) +
        countReal(path.nonWorkstationAssociates ?? []),
      0,
    );
    const unassigned = unassignedAssociates.length;
    return { scheduled: assigned + unassigned, assigned, unassigned, overrides: 0 };
  }, [processPaths, unassignedAssociates]);

  const handleToggleExpanded = (processPathId: string) => {
    setExpandedPaths((prev) => ({
      ...prev,
      [processPathId]: !(prev[processPathId] ?? true),
    }));
  };

  /**
   * Open the read-only Decision info popup for an assigned associate (the ℹ️
   * action). Resolves the associate from wherever they currently sit so the
   * popup can read their `decision`.
   */
  const handleOpenDecisionModal = (associateId: string) => {
    setViewingDecisionAssociate(findAssignedAssociate(processPaths, associateId));
  };

  const handleCloseDecisionModal = () => {
    setViewingDecisionAssociate(null);
  };

  return (
    <Box className="assignments-page" spacingInset="400">
      <StatsRow
        scheduled={stats.scheduled}
        assigned={stats.assigned}
        unassigned={stats.unassigned}
        overrides={stats.overrides}
      />

      <div className="assignments-page__layout">
        {/* Left column: unassigned associate sidebar. No assign action is
            supplied, so the rows carry no "Assign" button. */}
        <div className="assignments-page__sidebar">
          <AssociateSidebar
            unassignedAssociates={filteredUnassignedAssociates}
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
          />
          {paLeadHRAssociates.length > 0 && (
            <AssociateSidebar
              title="PA / Lead / HR"
              unassignedAssociates={filteredPALeadHRAssociates}
              searchQuery={paLeadHRSearchQuery}
              onSearchChange={setPALeadHRSearchQuery}
            />
          )}
        </div>

        {/* Right column: stacked process-path sections in read-only mode. Only
            the view-decision (ℹ️) action is wired; no assign/unassign/move. */}
        <div className="assignments-page__main">
          {processPaths.map((path) => (
            <ProcessPathSection
              key={path.fclmFunctionId}
              readOnly
              processPath={path}
              expanded={expandedPaths[path.fclmFunctionId] ?? true}
              onToggleExpanded={handleToggleExpanded}
              onViewDecision={handleOpenDecisionModal}
            />
          ))}
        </div>
      </div>

      {/* Footer: Last Modified info. No Verify, Submit, or Publish action. */}
      <div className="assignments-page__footer">
        <Text
          color="secondary"
          data-testid="published-assignment-last-modified"
        >
          Last Modified: {response.lastModifiedDate} ({response.lastModifiedBy}
          )
        </Text>
      </div>

      {/* Read-only decision info popup opened from an occupied slot's ℹ️
          action. Shows the associate's decision source + reason code (and
          optional free-text reason). Mutates nothing. */}
      <DecisionInfoModal
        open={viewingDecisionAssociate !== null}
        associate={viewingDecisionAssociate}
        onClose={handleCloseDecisionModal}
      />
    </Box>
  );
};

export default PublishedAssignmentScreen;
